<template>
    <div>
        <!-- Staff-authored free-form Today content (Roger's Trip App card).
             Full width, top billing, gone entirely when nothing is written. -->
        <div class="white-container mb-6" v-if="trip.app_today" v-html="trip.app_today"></div>

        <div class="columns">
        <div class="column is-two-thirds feed-column">
            <!-- Recent announcements outrank the itinerary — during a trip they
                 carry schedule changes and polls that need answers now. -->
            <today-announcements
                class="mb-6"
                :enabled="experienceEnabled"
                :posts="announcements"
                :latest-poll="latestPoll"
                :reg-id="registration.id"
                :is-staff="isStaff"
                @changed="$emit('changed')"
            />

            <!-- Departure day only: the countdown rides along until it hits zero. -->
            <div class="white-container pt-6 pb-6 mb-5 has-text-centered" v-if="isDepartureDay">
                <h4 class="has-text-primary mb-5">Be There. Stay There. Play There.</h4>

                <flip-countdown :deadline="trip.start_on"></flip-countdown>
            </div>

            <div v-if="trip.itinerary_visable">
                <div class="columns is-gapless is-mobile mb-3" v-if="todayDay">
                    <div class="column is-narrow">
                        <i class="fal fa-calendar-day fa-fw is-size-4 mr-3"></i>
                    </div>
                    <div class="column">
                        <h4>{{ todayIsCurrent ? 'Today' : 'Coming Up' }}</h4>
                    </div>
                </div>
                <itinerary-day :day="todayDay" expanded :highlight-key="nextEventKey" v-if="todayDay"/>

                <div v-if="tomorrowDay">
                    <div class="columns is-gapless is-mobile mb-3">
                        <div class="column is-narrow">
                            <i class="fal fa-calendar-plus fa-fw is-size-4 mr-3"></i>
                        </div>
                        <div class="column">
                            <h4>Tomorrow</h4>
                        </div>
                    </div>
                    <itinerary-day :day="tomorrowDay"/>
                </div>

                <ul class="sub-nav-menu mb-5 is-hidden-desktop">
                    <router-link :to="{name: 'Itinerary'}">
                        <li><i class="far fa-tennis-ball fa-fw mr-3"></i>View the Full Itinerary</li>
                    </router-link>
                </ul>
            </div>

        </div>
        <div class="column is-one-third is-hidden-touch rail-column">
            <!-- Same section-header anatomy as the left column so the white
                 containers start on the same line. -->
            <div class="columns is-gapless is-mobile mb-3">
                <div class="column is-narrow">
                    <i class="fal fa-link fa-fw is-size-4 mr-3"></i>
                </div>
                <div class="column">
                    <h4>Quick Links</h4>
                </div>
            </div>
            <ul class="sub-nav-menu">
                <router-link :to="{name: 'Itinerary'}">
                    <li><i class="far fa-tennis-ball fa-fw mr-3"></i>Full Itinerary</li>
                </router-link>
                <router-link :to="{name: 'Flights'}">
                    <li><i class="far fa-plane fa-fw mr-3"></i>Flights</li>
                </router-link>
                <router-link :to="{name: 'TripDocuments'}">
                    <li><i class="far fa-file-alt fa-fw mr-3"></i>Trip Documents</li>
                </router-link>
                <router-link :to="{name: 'AllTravelers'}">
                    <li><i class="far fa-users fa-fw mr-3"></i>Fellow Travelers</li>
                </router-link>
            </ul>

            <!-- #Experience teaser: read-only rows, the real thing is a tap away. -->
            <div class="mt-6" v-if="experienceEnabled && (feed.length || media.length)">
                <div class="columns is-gapless is-mobile mb-3">
                    <div class="column is-narrow">
                        <i class="fal fa-hashtag fa-fw is-size-4 mr-3"></i>
                    </div>
                    <div class="column">
                        <h4>Experience</h4>
                    </div>
                    <div class="column is-narrow ml-5">
                        <b-button type="is-primary" size="is-small" tag="router-link" :to="{name: 'Experience'}">See All<i class="fas fa-arrow-right ml-2"></i></b-button>
                    </div>
                </div>

                <div class="white-container p-4" v-if="feed.length">
                    <router-link :to="{name: 'Experience'}" v-for="post in feed" :key="post.id">
                        <div class="teaser-row">
                            <div class="columns is-mobile is-gapless">
                                <div class="column is-narrow">
                                    <div class="avatar" v-if="post.author_photo_file">
                                        <img :src="apiUrl + 'app/user/photo/' + post.author_photo_file"/>
                                    </div>
                                    <div class="avatar is-company" v-else-if="!post.user_id">
                                        <i class="fas fa-tennis-ball"></i>
                                    </div>
                                    <div class="avatar" v-else>
                                        <i class="fas fa-user"></i>
                                    </div>
                                </div>
                                <div class="column ml-2">
                                    <div>
                                        <span class="author">{{ post.author_name }}</span>
                                        <span class="is-size-7 has-text-grey ml-1">{{ post.created_at | moment('from') }}</span>
                                    </div>
                                    <div class="is-size-7 snippet">{{ snippet(post) }}</div>
                                </div>
                            </div>
                        </div>
                    </router-link>
                </div>

                <media-grid
                    class="mt-6"
                    :media="media"
                    :reg-id="registration.id"
                />
            </div>
        </div>
        </div>
    </div>
</template>

<script>
    import moment from 'moment';
    import {url} from '@/http_client';
    import FlipCountdown from 'vue2-flip-countdown';
    import ItineraryDay from '@/components/ItineraryDay.vue';
    import MediaGrid from '../experience/MediaGrid.vue';
    import TodayAnnouncements from './TodayAnnouncements.vue';

    export default {
        name: 'TodayActive',
        components: {FlipCountdown, ItineraryDay, MediaGrid, TodayAnnouncements},
        props: {
            trip: {type: Object, required: true},
            registration: {type: Object, required: true},
            itinerary: {type: Array, required: true},
            feed: {type: Array, default: () => []},
            media: {type: Array, default: () => []},
            announcements: {type: Array, required: true},
            latestPoll: {type: Object, default: null},
            experienceEnabled: {type: Boolean, default: false},
            isStaff: {type: Boolean, default: false}
        },
        data: function() {
            return {
                apiUrl: url,
                // Minute tick so "Up Next" rolls forward without a reload.
                now: new Date(),
                ticker: null
            };
        },
        computed: {
            isDepartureDay: function() {
                return moment(this.now).isSame(moment(this.trip.start_on), 'day');
            },
            // Device-local date. day_on is a plain date and during a trip the
            // phone is (almost always) in the trip's timezone; deriving a trip
            // timezone from event data isn't worth the edge cases.
            localToday: function() {
                return moment(this.now).format('YYYY-MM-DD');
            },
            todayDay: function() {
                const exact = this.itinerary.find(d => moment(d.day_on).format('YYYY-MM-DD') === this.localToday);
                if (exact) return exact;
                // Gap day (nothing scheduled today): show the next planned day.
                return this.itinerary.find(d => moment(d.day_on).format('YYYY-MM-DD') > this.localToday) || null;
            },
            todayIsCurrent: function() {
                return !!this.todayDay && moment(this.todayDay.day_on).format('YYYY-MM-DD') === this.localToday;
            },
            tomorrowDay: function() {
                if (!this.todayDay) return null;
                const after = moment(this.todayDay.day_on).format('YYYY-MM-DD');
                return this.itinerary.find(d => moment(d.day_on).format('YYYY-MM-DD') > after) || null;
            },
            nextEventKey: function() {
                // Only meaningful on the actual current day.
                if (!this.todayIsCurrent) return null;
                const nowUtc = moment.utc(this.now);
                for (const event of this.todayDay.events) {
                    if (event.active === '0') continue;
                    const startAt = event.type === 'override_event'
                        ? (event.day_event && event.day_event.start_at)
                        : event.start_at;
                    if (!startAt) continue;
                    if (moment.utc(startAt).isSameOrAfter(nowUtc)) {
                        return event.type + ':' + event.id;
                    }
                }
                return null;
            }
        },
        methods: {
            // One-line, tag-free preview of a post for the rail teaser.
            snippet: function(post) {
                const text = (post.body || '').replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
                let line = text;
                if (post.options && post.options.length) line = 'Poll: ' + (text || 'cast your vote');
                else if (!text && post.photos && post.photos.length) line = 'Shared a photo';
                return line.length > 90 ? line.substring(0, 90) + '…' : line;
            }
        },
        mounted() {
            this.ticker = setInterval(() => {
                this.now = new Date();
            }, 60 * 1000);
        },
        beforeDestroy() {
            clearInterval(this.ticker);
        }
    };
</script>

<style lang="scss" scoped>
    // Same breathing room between the main column and the rail as Experience.
    @media screen and (min-width: 1024px) {
        .feed-column {
            padding-right: 2rem;
        }

        .rail-column {
            padding-left: 1rem;
        }
    }

    .teaser-row {
        padding: 0.6rem 0.4rem;
        border-radius: 5px;

        &:hover {
            background-color: #f4f4f4;
        }

        .columns {
            align-items: flex-start;
        }

        .author {
            color: #363636;
            font-size: 0.85rem;
        }

        .snippet {
            color: #525252;
        }

        // Compact version of PostCard's avatar.
        .avatar {
            width: 30px;
            height: 30px;
            border-radius: 50%;
            overflow: hidden;
            border: 1px solid #e7e7e7;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: #f5f5f5;

            img {
                width: 100%;
                height: 100%;
                object-fit: cover;
            }

            i {
                color: #bebebe;
                font-size: 0.85rem;
            }

            &.is-company {
                background-color: #628b21;

                i {
                    color: white;
                }
            }
        }
    }

    a + a .teaser-row {
        border-top: 1px solid #eee;
        border-radius: 0 0 5px 5px;
    }
</style>
